import {
  isCodeIssueType,
  isIssueResolvedInScopeAndNotGlobally,
  isResolvedInCodeRepoAndNotResolved
} from "@/domain/issue"

import type { AttackChainsResponse } from "@/api/useTriage.types"
import type {
  ApplicativeImageFinding,
  BaseImageFinding,
  CodeRepoFinding,
  Finding,
  Issue,
  ThirdPartyFinding,
  VmFinding
} from "@/domain/issue"
export const getFindingsInfoForExpandRow = (
  response: KosmosApi.Response<Issue>
) => {
  const data = response?.data?.findings?.map((finding) => {
    if (!!finding.importPaths && !finding.importPaths.length)
      return {
        ...finding,
        importPaths: [
          [
            {
              name: finding.packageName,
              // A finding can hold several copies of the package, so name every version.
              version: finding.packageVersions?.join(", ") ?? ""
            }
          ]
        ]
      }

    return finding
  })
  const issue = response?.data
    ? ({ ...response.data, findings: data } as Issue)
    : response?.data
  const finding: Finding = data?.[0]
  const emptyData = {
    codeRepoFindings: [],
    baseImageFindings: [],
    thirdPartyFindings: [],
    imageFindings: [],
    fixVersions: [],
    vmFindings: []
  }

  if (!finding) {
    return {
      metadata: {
        epss: 0,
        cvss: 0,
        packageName: "",
        packageVersions: [""],
        cwe: { id: "", description: "" },
        language: "unknown" as const,
        issueType: "Vulnerable_Package" as const,
        issue: issue
      },
      ...emptyData
    }
  }

  const vmFindings = data.filter(
    (item) => (item.resource.type as any) === "Vm"
  ) as VmFinding[]

  if (isCodeIssueType(finding.issueType)) {
    return { metadata: finding, ...emptyData, vmFindings }
  }

  const {
    cvss,
    epss,
    packageVersions,
    packageName,
    fixVersions,
    cwe,
    language,
    issueType
  } = finding
  const isResolvedInCodeRepoOnly = isResolvedInCodeRepoAndNotResolved(issue)
  const codeRepoFindings = data
    .filter((item) => item.resource.type === "CodeRepository")
    .map((item) => ({ isResolvedInCodeRepoOnly, ...item })) as CodeRepoFinding[]
  // A finding's insights (fromThirdParty / fromBaseImage) are independent of
  // its remedy shape: the backend can persist an ImagePackageFix (dockerfile fix) on a
  // finding with either insight true. Route by remedy type so the matching fix renderer
  // gets the data it expects.
  const hasImagePackageRemedies = (item: Finding) => {
    const remedies = (item as ApplicativeImageFinding).remedies
    return remedies?.length > 0 && "packageName" in remedies[0]
  }
  // Third party takes priority over base image (a finding can have both insights true)
  const thirdPartyFindings = data.filter(
    (item) =>
      item.resource.type === "Image" &&
      item.introducedThroughInsights?.fromThirdParty &&
      !hasImagePackageRemedies(item)
  ) as ThirdPartyFinding[]
  const baseImageFindings = data.filter(
    (item) =>
      item.resource.type === "Image" &&
      item.introducedThroughInsights?.fromBaseImage &&
      !item.introducedThroughInsights?.fromThirdParty &&
      !hasImagePackageRemedies(item)
  ) as BaseImageFinding[]
  const imageFindings = data.filter(
    (item) =>
      item.resource.type === "Image" &&
      (hasImagePackageRemedies(item) ||
        (!item.introducedThroughInsights?.fromThirdParty &&
          !item.introducedThroughInsights?.fromBaseImage))
  ) as ApplicativeImageFinding[]

  return {
    metadata: {
      cvss,
      epss,
      packageVersions,
      packageName,
      cwe,
      language,
      issueType
    },
    fixVersions,
    codeRepoFindings,
    baseImageFindings,
    thirdPartyFindings,
    imageFindings,
    vmFindings,
    issue, // Include the full issue data
    showResolvedFindingsAlert: isIssueResolvedInScopeAndNotGlobally(issue)
  }
}

export const getAttackChains = (response: AttackChainsResponse) => {
  const { data, ...rest } = response

  return {
    data: data.map((attack) => ({
      application: { name: "application_name", id: "123" },
      environment: "production",
      namespace: "cloud",
      ...attack
    })),
    ...rest
  }
}
